import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EntityWorkspace from '../components/EntityWorkspace';
import EntityPivotLink from '../components/EntityPivotLink';
import DeleteButton from '../components/DeleteButton';
import api from '../services/api';
import useEvidenceTimezone from '../hooks/useEvidenceTimezone';

const MARKING_OPTIONS = [
  'TLP:CLEAR', 'TLP:GREEN', 'TLP:AMBER', 'TLP:AMBER+STRICT', 'TLP:RED',
];

function markingClass(marking) {
  return `marking-pill marking-${(marking || 'TLP:CLEAR').replace(/[+:]/g, '-').toLowerCase()}`;
}

function buildIocWorkspace({ kind, title, subtitle, breadcrumbs }) {
  return function IocWorkspacePage() {
    const navigate = useNavigate();
    const { format, suffix } = useEvidenceTimezone();
    const defaultFilters = useMemo(() => ({ kind }), [kind]);

    const columns = useMemo(() => [
      {
        key: 'type',
        label: 'Type',
        render: (row) => <span className="entity-type-chip">{row.ioc_type}</span>,
      },
      {
        key: 'value',
        label: 'Value',
        render: (row) => (
          <EntityPivotLink type="indicator" id={row.indicator_id} label={row.value} showBadge={false} />
        ),
      },
      {
        key: 'marking',
        label: 'Marking',
        render: (row) => <span className={markingClass(row.marking)}>{row.marking || 'TLP:CLEAR'}</span>,
      },
      {
        key: 'labels',
        label: 'Labels',
        render: (row) => (
          <div className="entity-tag-list">
            {(row.labels || row.tags || []).slice(0, 3).map((tag) => (
              <span key={tag} className="entity-tag">{tag}</span>
            ))}
          </div>
        ),
      },
      {
        key: 'source_material',
        label: 'Source',
        render: (row) => {
          const link = row.source_link;
          const url = row.source_url || link?.url;
          const label = link?.label || link?.title || row.source;
          if (!label && !url) return '—';
          if (url) {
            return (
              <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
                {label || url}
              </a>
            );
          }
          return label;
        },
      },
      {
        key: 'case',
        label: 'Case',
        render: (row) => (
          row.case_id ? (
            <EntityPivotLink type="case" id={row.case_id} label={row.case_title || row.case_id} showBadge={false} />
          ) : '—'
        ),
      },
      {
        key: 'confidence',
        label: 'Confidence',
        render: (row) => row.confidence,
      },
      {
        key: 'last_seen',
        label: `Last seen (${suffix})`,
        render: (row) => <span className="timestamp-cell">{format(row.last_seen)}</span>,
      },
    ], [format, suffix]);

    const filterFields = useMemo(() => [
      {
        key: 'ioc_type',
        label: 'Type',
        placeholder: 'All types',
        options: (meta) => (meta?.ioc_types || []).map((value) => ({
          value,
          label: meta?.ioc_type_labels?.[value] || value,
        })),
      },
      {
        key: 'confidence',
        label: 'Confidence',
        placeholder: 'All confidence',
        options: (meta) => meta?.confidence_levels || [],
      },
      {
        key: 'marking',
        label: 'Marking',
        placeholder: 'All markings',
        staticOptions: MARKING_OPTIONS,
      },
    ], []);

    return (
      <EntityWorkspace
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        entityType={kind}
        apiPath="/hdp/iocs"
        responseKey="iocs"
        defaultFilters={defaultFilters}
        filterFields={filterFields}
        columns={columns}
        selectable
        bulkActions={
          kind === 'observable'
            ? [
                { action: 'promote', label: 'Promote to indicator', className: 'btn-secondary' },
                { action: 'update_marking', label: 'Mark TLP:AMBER', payload: { marking: 'TLP:AMBER' } },
                { action: 'delete', label: 'Delete', className: 'btn-danger' },
              ]
            : [
                { action: 'demote', label: 'Move to observables', className: 'btn-secondary' },
                { action: 'update_marking', label: 'Mark TLP:AMBER', payload: { marking: 'TLP:AMBER' } },
                { action: 'delete', label: 'Delete', className: 'btn-danger' },
              ]
        }
        rowIdField="indicator_id"
        onRowClick={(row) => navigate(`/indicators/${row.indicator_id}`)}
        rowActions={(row, reload) => (
          <DeleteButton
            confirmMessage={`Delete ${kind} "${row.value}"?`}
            onDelete={async () => {
              await api.delete(`/hdp/iocs/${row.indicator_id}`);
              reload();
            }}
          />
        )}
        emptyMessage={`No ${kind}s match your filters.`}
      />
    );
  };
}

export const Observables = buildIocWorkspace({
  kind: 'observable',
  title: 'Observables',
  subtitle: 'Raw observations extracted from evidence and enrichment — promote to indicators when validated.',
  breadcrumbs: [
    { label: 'Dashboard', to: '/' },
    { label: 'Observables' },
  ],
});

export const IndicatorsWorkspace = buildIocWorkspace({
  kind: 'indicator',
  title: 'Indicators',
  subtitle: 'Validated threat indicators linked to cases and actors.',
  breadcrumbs: [
    { label: 'Dashboard', to: '/' },
    { label: 'Indicators' },
  ],
});

export default Observables;
